import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../../App/redux/store';
import {
  createEntity,
  fetchDatas,
  updateEntity,
} from '../../../services/axiosInstanceService';
import { appManagerActions } from '../../../store/appManager/actions';
import { usersNotifications } from '../notifications';
import { userParser, usersParser } from '../usersParser';
import { fetchUserMarketResearch } from './usersRequests';

/**
 * Asynchronous thunk to fetch users for a specific company.
 * This function retrieves user data from the API based on the provided company ID.
 *
 * @param {Object} params - Parameters object.
 * @param {string | null} params.companyIdIri - The IRI of the company whose users should be fetched.
 * @returns {Promise<Users>} A promise that resolves to the parsed user data or rejects with an error.
 */
const fetchUsersThunk = createAsyncThunk(
  'users/fetchUsersThunk',
  async (params: { companyIdIri: string | null }) => {
    try {
      // Validate if companyIdIri is provided
      if (!params.companyIdIri) throw new Error('Company idIri not found');

      // Fetch user data from the API using the company ID
      const result = await fetchDatas(params.companyIdIri + '/users');

      // Parse the fetched data to match the expected structure
      const parsed = usersParser(result['hydra:member']);

      // Return the parsed user data
      return parsed;
    } catch (error) {
      // Reject the promise with the caught error
      return Promise.reject(error);
    }
  },
);

/**
 * Asynchronous thunk to create a new user.
 * This function sends user data to the API and returns the created user.
 *
 * @param {Object} params - Parameters object.
 * @param {Object} params.body - The user data required for creation.
 * @param {string} params.body.firstName - The first name of the user (required).
 * @param {string} params.body.lastName - The last name of the user.
 * @param {string} params.body.email - The email of the user (required).
 * @param {string} [params.body.phoneNumber] - The phone number of the user (optional).
 * @param {boolean} [params.body.enableNewsLetter] - Whether the user is subscribed to the newsletter.
 * @returns {Promise<any>} A promise that resolves to the created user data or rejects with an error.
 */
const createUserThunk = createAsyncThunk(
  'users/createUserThunk',
  async (params: { body: CreateUserParams }, { getState, dispatch }) => {
    try {
      const state = getState() as RootState;
      const { companyIdIri } = state.company;
      // Validate required user properties and company idIri
      if (!companyIdIri) throw new Error('Company idIri param missing');
      if (!params.body.firstName) throw new Error('User firstName param missing');
      if (!params.body.lastName) throw new Error('User lastName param missing');
      if (!params.body.email) throw new Error('User email param missing');

      // Extract and transform user data to match API expectations
      const b = params.body;
      const parsedBody = {
        company: companyIdIri,
        enabled: true,
        first_name: b.firstName,
        last_name: b.lastName,
        email: b.email,
        phone_number: b.phoneNumber, // Include phone number if provided
      };

      // Send the user data to the API
      const result = await createEntity({ endpoint: '/users', body: parsedBody });

      // Parse the API response into the expected format
      const parsed = userParser({
        data: result,
      });

      dispatch(
        appManagerActions.actionNotificationDisplay.visible(
          usersNotifications
            .create(`${parsed.user.firstName} ${parsed.user.lastName}`)
            .success(),
        ),
      );
      // Return the parsed user data
      return parsed.user;
    } catch (error) {
      dispatch(
        appManagerActions.actionNotificationDisplay.visible(
          usersNotifications.create().failure(),
        ),
      );
      // Reject the promise with the caught error
      return Promise.reject(error);
    }
  },
);

/**
 * Async thunk to update a user entity.
 *
 * This function updates a user's information by sending a request to the API.
 * The provided user data is transformed to match the API expectations before being sent.
 * If the update is successful, a notification is dispatched and the updated user data is returned.
 * If an error occurs, an error notification is dispatched and the promise is rejected.
 *
 * @param {Object} params - The parameters required to update the user.
 * @param {string} params.idIri - The unique IRI identifier of the user.
 * @param {UpdateUserParams} params.body - The user data to update.
 * @param {Object} thunkAPI - The thunkAPI object, allowing access to `dispatch`.
 * @param {Function} thunkAPI.dispatch - Redux dispatch function.
 * @returns {Promise<IUser>} The updated user entity.
 * @throws {Error} Throws an error if `idIri` or `body` is missing.
 */
const updateUserThunk = createAsyncThunk(
  'users/updateUserThunk',
  async (params: { idIri: string; body: UpdateUserParams }, { dispatch }) => {
    try {
      // Validate required user properties
      if (!params.idIri) throw new Error('IdIri not found');
      if (!params.body) throw new Error('Body not found');

      // Extract and transform user data to match API expectations
      const b = params.body;
      const parsedBody = {
        first_name: b.firstName, // Convert camelCase to snake_case
        last_name: b.lastName,
        email: b.email,
        phone_number: b.phoneNumber, // Include phone number if provided
        enabled_newsletter: b.enableNewsLetter, // Default to false if not provided
        enabled: b.enabled, // Default to false if not provided
      };

      // Send the user data to the API
      const result = await updateEntity({ idIri: params.idIri, body: parsedBody });

      // Parse the API response into the expected format
      const parsed = userParser({ data: result });

      // Dispatch a success notification with the updated user name
      dispatch(
        appManagerActions.actionNotificationDisplay.visible(
          usersNotifications
            .update(`${parsed.user.firstName} ${parsed.user.lastName}`)
            .success(),
        ),
      );

      // Return the parsed user data
      return parsed.user;
    } catch (error) {
      // Dispatch a failure notification if the update fails
      dispatch(
        appManagerActions.actionNotificationDisplay.visible(
          usersNotifications.update().failure(),
        ),
      );

      // Reject the promise with the caught error
      return Promise.reject(error);
    }
  },
);

/**
 * Asynchronous thunk to fetch user market research data.
 * This function retrieves market research data from the API based on the provided user ID.
 *
 * @param {Object} params - Parameters object.
 * @param {string} params.userIdIri - The IRI of the user whose market research should be fetched.
 * @returns {Promise<IMarketResearch>} A promise that resolves to the parsed market research data or rejects with an error.
 */
const fetchUserMarketResearchThunk = createAsyncThunk(
  'users/fetchUserMarketResearchThunk',
  async (params: { userIdIri: string }) => {
    try {
      // Validate if userIdIri is provided
      if (!params.userIdIri) throw new Error('User idIri not found');

      // Fetch market research data from the API using the user ID
      const marketResearch = await fetchUserMarketResearch(params.userIdIri);

      // Return the market research data
      return marketResearch;
    } catch (error) {
      // Reject the promise with the caught error
      return Promise.reject(error);
    }
  },
);

export {
  createUserThunk,
  fetchUsersThunk,
  fetchUserMarketResearchThunk,
  updateUserThunk,
};
